import { useEnabledMfrrProductionUnits } from 'api/mfrr/mfrr.api'
import type { EnabledMfrrProductionUnit } from 'api/mfrr/mfrr.types'
import { ReactNode, useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import mfrrStore, { MfrrDateRangeOption, setMfrrSelectedPeriod } from 'store/mfrr/mfrr'
import { Tabs } from 'ui/atoms'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { useSnapshot } from 'valtio'
import MFRRTradingViewHeader from './components/Header/MFRRTradingViewHeader'
import { MFRRMarketResults } from './components/MFRRCMResultsTrading/MFRRCMResultsTrading'
import { MFRREAMTrading } from './components/MFRREAMTrading/MFRREAMTrading'
import { MFRRCMTrading } from './components/MRFFCMTrading/MFRRCMTrading'
import styles from './MFRRTradingView.module.less'
import type { MfrrDateRangeSwitchItem } from './MFRRTradingView.types'
import { useViewedMfrrBidWindow } from './utils/hooks/useViewedMfrrBidWindow'

enum TAB_ID {
  MFRR_CM = 'mFRR_cm',
  MFRR_EAM = 'mFRR_eam',
  MFRR_CM_RESULTS = 'mFRR_cm_results',
}

// Stable reference: a fresh `[]` on every render while the query is pending/disabled would
// make the dependent useMemo/useEffect chain in MfrrBidTable re-fire endlessly.
const EMPTY_MFRR_UNITS: EnabledMfrrProductionUnit[] = []

export default function MFRRTradingView(): ReactNode {
  const { t } = useTranslation()
  const { activeSystem } = useAuth()
  const [tabId, setTabId] = useState<string>('')
  const {
    data: enabledMfrrProductionUnits = EMPTY_MFRR_UNITS,
    isPending: isMfrrCmUnitsLoading,
    error: mfrrCmUnitsError,
  } = useEnabledMfrrProductionUnits(activeSystem?.id, 'cm', !!activeSystem)

  const {
    data: enabledMfrrEamUnits = EMPTY_MFRR_UNITS,
    isPending: isMfrrEamUnitsLoading,
    error: mfrrEamUnitsError,
  } = useEnabledMfrrProductionUnits(activeSystem?.id, 'eam', !!activeSystem)

  const submoduleToTabItemMap = {
    [TAB_ID.MFRR_CM]: {
      id: TAB_ID.MFRR_CM,
      title: t('mFRR-CM'),
      children: [],
    },
    [TAB_ID.MFRR_EAM]: {
      id: TAB_ID.MFRR_EAM,
      title: t('mFRR-EAM'),
      children: [],
    },
    [TAB_ID.MFRR_CM_RESULTS]: {
      id: TAB_ID.MFRR_CM_RESULTS,
      title: t('CM Energy trade results'),
      children: [],
    },
  }

  const tabs = Object.entries(submoduleToTabItemMap).map(([_, value]) => value)

  const switchItems: MfrrDateRangeSwitchItem[] = [
    { value: 'today', label: t('Today') },
    { value: 'tomorrow', label: t('Tomorrow') },
    { value: 'dayAfterTomorrow', label: t('Day after tomorrow') },
    { value: 'custom', label: t('Custom') },
  ]

  const { selectedPeriod } = useSnapshot(mfrrStore)
  const switchValue = selectedPeriod.switchValue
  const date = useMemo(
    () => ({ startTime: selectedPeriod.startTime, endTime: selectedPeriod.endTime }),
    [selectedPeriod.startTime, selectedPeriod.endTime]
  )

  const mfrrCmUnits = useMemo(() => {
    return enabledMfrrProductionUnits.filter((unit) => unit.is_enabled)
  }, [enabledMfrrProductionUnits])

  const hasMfrrCmUnitsError = Boolean(mfrrCmUnitsError)
  const isMfrrCmUnitsPending = isMfrrCmUnitsLoading

  const mfrrEamUnits = useMemo(() => {
    return enabledMfrrEamUnits.filter((unit) => unit.is_enabled)
  }, [enabledMfrrEamUnits])
  const hasMfrrEamUnitsError = Boolean(mfrrEamUnitsError)
  const isMfrrEamUnitsPending = isMfrrEamUnitsLoading

  const setDateCallback = useCallback(
    (startTime: ISODateTime, endTime: ISODateTime) => {
      setMfrrSelectedPeriod({ switchValue, startTime, endTime })
    },
    [switchValue]
  )

  const setSwitchValue = useCallback(
    (value: MfrrDateRangeOption) => {
      setMfrrSelectedPeriod({ switchValue: value, startTime: date.startTime, endTime: date.endTime })
    },
    [date.startTime, date.endTime]
  )

  const headerRef = useRef<HTMLDivElement>(null)
  const activeMarket = tabId === TAB_ID.MFRR_CM ? 'cm' : tabId === TAB_ID.MFRR_EAM ? 'eam' : null

  useViewedMfrrBidWindow(activeMarket, date.startTime)

  return (
    <div className={styles.MFRRTradingView}>
      <MFRRTradingViewHeader
        infoHeader={t('mFRR')}
        switchItems={switchItems}
        switchValue={switchValue}
        onSwitchValueChange={setSwitchValue}
        date={date}
        setDate={setDateCallback}
        activeMarket={activeMarket}
      >
        <div className={styles.MFRRTradingView_Header} ref={headerRef}></div>
        <div className={styles.MFRRTradingView_Tabs}>
          <Tabs
            items={tabs}
            onChange={(_, id) => {
              setTabId(id as string)
            }}
            innerLeftPadding
            marginBottom={false}
            uniqueStorageKey={`MFRRTradingViewTabs`}
          />
        </div>
      </MFRRTradingViewHeader>
      {tabId === TAB_ID.MFRR_CM && (
        <div className={styles.MFRRTradingView_Content}>
          <MFRRCMTrading
            units={mfrrCmUnits}
            systemId={activeSystem?.id}
            dateStartTime={date.startTime}
            hasConfigError={hasMfrrCmUnitsError}
            isConfigLoading={isMfrrCmUnitsPending}
          />
        </div>
      )}
      {tabId === TAB_ID.MFRR_EAM && (
        <div className={styles.MFRRTradingView_Content}>
          <MFRREAMTrading
            units={mfrrEamUnits}
            cmUnits={mfrrCmUnits}
            systemId={activeSystem?.id}
            dateStartTime={date.startTime}
            hasConfigError={hasMfrrEamUnitsError}
            isConfigLoading={isMfrrEamUnitsPending}
          />
        </div>
      )}
      {tabId === TAB_ID.MFRR_CM_RESULTS && (
        <div className={styles.MFRRTradingView_Content}>
          <MFRRMarketResults dateStartTime={date.startTime} />
        </div>
      )}
    </div>
  )
}
